import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isSameWeek,
  isSameYear,
  startOfWeek,
} from "date-fns";
import { fr } from "date-fns/locale";
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  Phone,
  Search,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatPhone } from "@/lib/phone";
import {
  getCallHistory,
  type CallHistoryEntry,
  type CallHistoryOutcome,
  type CallHistoryPeriod,
  type CallHistorySummary,
} from "@/api/callCenter";
import { usePermissions } from "@/hooks/usePermissions";
import { ALL_MEMBERS, CallUserFilter } from "./CallUserFilter";
import { HiddenDetail, isHidden } from "@/components/shared/HiddenDetail";

interface CallHistoryTabProps {
  slug: string;
}

/** Server page size — kept in sync with the `page_size` the backend
 *  defaults to so the pagination math (`totalPages`) is correct. */
const PAGE_SIZE = 50;

/** Debounce for the search box — the value is sent to the backend,
 *  so we wait for a typing pause before firing the request. */
const SEARCH_DEBOUNCE_MS = 300;

/** date-fns weeks start on Monday across this tab (label + the
 *  "same week" check that disables the next arrow). */
const WEEK_OPTS = { weekStartsOn: 1 } as const;

/** The four windows the History tab can aggregate over. Order matches
 *  the segmented control left-to-right. */
const PERIOD_OPTIONS: Array<{ value: CallHistoryPeriod; label: string }> = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "year", label: "Year" },
];

/** Per-outcome label + badge palette. Interested=green, Neutral=gray,
 *  Not Interested=red, No Answer=orange — the brief's colour spec.
 *  Unknown outcomes (legacy rows) fall back to a neutral badge. */
const OUTCOME_META: Record<string, { label: string; badge: string }> = {
  interested: { label: "Interested", badge: "bg-emerald-50 text-emerald-700" },
  neutral: { label: "Neutral", badge: "bg-slate-100 text-slate-600" },
  not_interested: {
    label: "Not Interested",
    badge: "bg-rose-50 text-rose-700",
  },
  no_answer: { label: "No Answer", badge: "bg-orange-50 text-orange-600" },
};

const OUTCOME_OPTIONS: Array<{ value: CallHistoryOutcome; label: string }> = [
  { value: "interested", label: "Interested" },
  { value: "neutral", label: "Neutral" },
  { value: "not_interested", label: "Not Interested" },
  { value: "no_answer", label: "No Answer" },
];

/** Truncate company notes for the table cell — the brief caps the
 *  excerpt at 50 characters. */
function excerpt(text: string | null): string {
  if (!text) return "";
  const trimmed = text.trim();
  return trimmed.length > 50 ? `${trimmed.slice(0, 50)}…` : trimmed;
}

/** Time shown in the first table column. For the day view a bare
 *  `HH:mm` is enough; wider windows span several days, so the date
 *  is prepended to keep rows unambiguous. */
function formatRowTime(iso: string, period: CallHistoryPeriod): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return period === "day"
    ? format(d, "HH:mm")
    : format(d, "d MMM, HH:mm", { locale: fr });
}

function capitalize(s: string): string {
  return s.length > 0 ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/** Shift `date` by one window in `dir` (-1 back, +1 forward). */
function shiftPeriod(
  period: CallHistoryPeriod,
  date: Date,
  dir: -1 | 1,
): Date {
  switch (period) {
    case "day":
      return addDays(date, dir);
    case "week":
      return addWeeks(date, dir);
    case "month":
      return addMonths(date, dir);
    case "year":
      return addYears(date, dir);
  }
}

/** True when `date` sits in the window that contains today — used to
 *  disable the "next" arrow / "Today" button (no browsing the
 *  future). */
function isCurrentPeriod(period: CallHistoryPeriod, date: Date): boolean {
  const now = new Date();
  switch (period) {
    case "day":
      return isSameDay(date, now);
    case "week":
      return isSameWeek(date, now, WEEK_OPTS);
    case "month":
      return isSameMonth(date, now);
    case "year":
      return isSameYear(date, now);
  }
}

/** Human label for the active window: "Lundi 19 Mai 2026",
 *  "19 – 25 mai 2026", "Mai 2026" or "2026". Computed locally so the
 *  label is available immediately, before the request resolves. */
function periodLabel(period: CallHistoryPeriod, date: Date): string {
  switch (period) {
    case "day":
      return capitalize(format(date, "EEEE d MMMM yyyy", { locale: fr }));
    case "week": {
      const start = startOfWeek(date, WEEK_OPTS);
      const end = endOfWeek(date, WEEK_OPTS);
      const range = isSameMonth(start, end)
        ? `${format(start, "d", { locale: fr })} – ${format(end, "d MMMM yyyy", { locale: fr })}`
        : `${format(start, "d MMM", { locale: fr })} – ${format(end, "d MMM yyyy", { locale: fr })}`;
      return capitalize(range);
    }
    case "month":
      return capitalize(format(date, "MMMM yyyy", { locale: fr }));
    case "year":
      return format(date, "yyyy");
  }
}

/** History tab — a log of the project's logged calls over a chosen
 *  window. The day/week/month/year selector picks the window size;
 *  the arrows / "Today" walk it. Filter with the search box (company
 *  / contact / phone) and the outcome chips. A recap row under the
 *  table breaks the window down by outcome. Gracefully degrades to an
 *  empty state when the backend endpoint isn't available yet. */
export function CallHistoryTab({ slug }: CallHistoryTabProps) {
  const { canViewCallCenterTeam } = usePermissions();
  const [period, setPeriod] = useState<CallHistoryPeriod>("day");
  const [selectedDate, setSelectedDate] = useState<Date>(() => new Date());
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [outcome, setOutcome] = useState<CallHistoryOutcome | null>(null);
  const [selectedUserId, setSelectedUserId] = useState(ALL_MEMBERS);
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<CallHistoryEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<CallHistorySummary | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [available, setAvailable] = useState(true);

  const dateParam = format(selectedDate, "yyyy-MM-dd");
  const label = periodLabel(period, selectedDate);
  const atCurrent = isCurrentPeriod(period, selectedDate);

  // Only privileged users may scope to another operator; for everyone
  // else we send no user_id and the backend pins results to them.
  const userIdParam = canViewCallCenterTeam ? selectedUserId : undefined;

  // Debounce the search box before it reaches the request.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  // Any filter change rewinds to page 1 — page N rarely exists under
  // the new filter set.
  useEffect(() => {
    setPage(1);
  }, [period, dateParam, debouncedSearch, outcome, userIdParam]);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    getCallHistory(slug, {
      period,
      date: dateParam,
      search: debouncedSearch,
      outcome,
      userId: userIdParam,
      page,
      page_size: PAGE_SIZE,
    }).then((res) => {
      if (cancelled) return;
      if (!res) {
        setAvailable(false);
        setItems([]);
        setTotal(0);
        setSummary(null);
      } else {
        setAvailable(true);
        setItems(res.items);
        setTotal(res.total);
        setSummary(res.summary);
      }
      setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [slug, period, dateParam, debouncedSearch, outcome, userIdParam, page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      {/* Period selector + operator filter + search */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1">
            {PERIOD_OPTIONS.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => setPeriod(p.value)}
                className={cn(
                  "rounded-md px-3 py-1.5 text-xs font-medium transition-all",
                  period === p.value
                    ? "bg-white text-slate-900 shadow-sm-tile"
                    : "text-slate-500 hover:text-slate-700",
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
          {canViewCallCenterTeam && (
            <CallUserFilter
              slug={slug}
              value={selectedUserId}
              onChange={setSelectedUserId}
            />
          )}
        </div>
        <div className="relative w-full max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by company, contact or phone..."
            className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
        </div>
      </div>

      {/* Window navigation */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setSelectedDate((d) => shiftPeriod(period, d, -1))}
            aria-label={`Previous ${period}`}
            className="rounded-lg border border-slate-200 bg-white p-1.5 text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="min-w-[220px] text-center text-sm font-semibold text-slate-900">
            {label}
          </span>
          <button
            type="button"
            onClick={() => setSelectedDate((d) => shiftPeriod(period, d, 1))}
            disabled={atCurrent}
            aria-label={`Next ${period}`}
            className="rounded-lg border border-slate-200 bg-white p-1.5 text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700 disabled:opacity-40 disabled:hover:bg-white"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <button
          type="button"
          onClick={() => setSelectedDate(new Date())}
          disabled={atCurrent}
          className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-white"
        >
          Today
        </button>
      </div>

      {/* Outcome filter */}
      <div className="flex flex-wrap items-center gap-2">
        <OutcomeChip
          active={outcome === null}
          onClick={() => setOutcome(null)}
          label="All"
        />
        {OUTCOME_OPTIONS.map((o) => (
          <OutcomeChip
            key={o.value}
            active={outcome === o.value}
            onClick={() => setOutcome(o.value)}
            label={o.label}
          />
        ))}
      </div>

      {!loaded && (
        <div className="surface-plane flex items-center justify-center py-16 text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      )}

      {loaded && !available && (
        <div className="surface-plane p-8 text-center">
          <p className="text-sm text-slate-600">
            Call history isn&rsquo;t available yet for this project.
          </p>
          <p className="mt-1 text-xs text-slate-400">
            Once the backend rolls out the call-history endpoint, this tab
            will populate.
          </p>
        </div>
      )}

      {loaded && available && items.length === 0 && (
        <div className="surface-plane p-10 text-center">
          <Phone className="mx-auto h-6 w-6 text-slate-300" />
          <p className="mt-2 text-sm text-slate-600">
            No calls logged for this {period}
          </p>
          <p className="mt-1 text-xs text-slate-400">
            Use the arrows above to browse another {period}.
          </p>
        </div>
      )}

      {loaded && available && items.length > 0 && (
        <>
          <div className="overflow-hidden rounded-plane bg-white shadow-plane">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-900/[0.06] bg-slate-50 text-[11px] uppercase tracking-wide text-slate-400">
                  <th className="px-4 py-2.5 font-medium">
                    {period === "day" ? "Time" : "Date"}
                  </th>
                  <th className="px-4 py-2.5 font-medium">Company</th>
                  <th className="px-4 py-2.5 font-medium">Contact</th>
                  <th className="px-4 py-2.5 font-medium">Phone</th>
                  <th className="px-4 py-2.5 font-medium">Outcome</th>
                  <th className="px-4 py-2.5 font-medium">Notes</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => {
                  const meta =
                    (row.outcome && OUTCOME_META[row.outcome]) || null;
                  return (
                    <tr
                      key={row.id}
                      className="border-b border-slate-900/[0.06] transition-colors duration-150 last:border-0 hover:bg-indigo-500/[0.04]"
                    >
                      <td className="whitespace-nowrap px-4 py-2.5 font-mono text-xs text-slate-500">
                        {formatRowTime(row.created_at, period)}
                      </td>
                      <td className="px-4 py-2.5">
                        <Link
                          to={`/projects/${slug}/companies/${row.company_id}`}
                          className="font-medium text-indigo-600 hover:text-indigo-800 hover:underline"
                        >
                          {row.company_name}
                        </Link>
                      </td>
                      <td className="px-4 py-2.5 text-slate-700">
                        {row.contact_name || "—"}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 font-mono text-xs text-slate-600">
                        {isHidden(row, "contact_phone") ? (
                          <HiddenDetail label="Phone" className="font-sans" />
                        ) : (
                          formatPhone(row.contact_phone)
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <span
                          className={cn(
                            "inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
                            meta ? meta.badge : "bg-slate-100 text-slate-500",
                          )}
                        >
                          {meta ? meta.label : (row.outcome ?? "—")}
                        </span>
                      </td>
                      <td
                        className="max-w-[220px] px-4 py-2.5 text-slate-500"
                        title={row.notes ?? undefined}
                      >
                        {excerpt(row.notes)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Window recap */}
          {summary && (
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3 text-sm text-slate-600">
              <span className="font-medium text-slate-700">Summary:</span>{" "}
              {summary.calls} call{summary.calls === 1 ? "" : "s"} ·{" "}
              {summary.interested} interested · {summary.neutral} called ·{" "}
              {summary.no_answer} no answer · {summary.not_interested} not
              interested
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-end gap-2 text-xs text-slate-500">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 transition-colors hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                Prev
              </button>
              <span>
                Page {page} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 transition-colors hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent"
              >
                Next
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function OutcomeChip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-3 py-1 text-xs font-medium transition-all",
        active
          ? "border-indigo-200 bg-indigo-50 text-indigo-700"
          : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700",
      )}
    >
      {label}
    </button>
  );
}
